import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import {
    ChartListItem,
    LendingEquipmentUsesVolumeByProductListChart,
    splitRankedChartSeries,
    takeRankedChartSeries,
    toChartListItems
} from '@frontend/chart/components';
import { Chart } from '@frontend/chart/types';
import { useEffect, useState } from 'react';

import { getLast30DaysChartRange } from '../../../statistics/pudo/last-30-days-chart-range';
import { useInsightDetailMode } from '../insight-detail-mode.context';
import { useInsightFetchRange } from '../insight-detail/use-insight-fetch-range';

/** Ranked preview size on the insights overview card (most and least). */
const OVERVIEW_PRODUCT_LIMIT = 4;

const toUsageItems = (chart: Chart, unknownLabel: string): ChartListItem[] => toChartListItems(chart, unknownLabel).filter((item) => item.value > 0);

export const useLendingEquipmentUsesVolumeByProductList = (accountId: string, unknownLabel: string) => {
    const isDetailMode = useInsightDetailMode();
    const [highestItems, setHighestItems] = useState<ChartListItem[]>([]);
    const [lowestItems, setLowestItems] = useState<ChartListItem[]>([]);
    const [detailItems, setDetailItems] = useState<ChartListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRange = useInsightFetchRange(getLast30DaysChartRange);

    useEffect(() => {
        if (!accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = fetchRange;

        ChartClient.fetchChartData(accountId, LendingEquipmentUsesVolumeByProductListChart.definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            ...fetchRange.entityParams,
            [DefaultQueryParams.SORTDIRECTION]: 'desc'
        })
            .then((chart) => {
                if (cancelled) return;

                if (isDetailMode) {
                    const ranked = takeRankedChartSeries(chart, 'highest', Number.POSITIVE_INFINITY);
                    setDetailItems(toUsageItems(ranked, unknownLabel));
                    setHighestItems([]);
                    setLowestItems([]);
                    return;
                }

                const { highest, lowest } = splitRankedChartSeries(chart, OVERVIEW_PRODUCT_LIMIT);
                setHighestItems(toUsageItems(highest, unknownLabel));
                setLowestItems(toUsageItems(lowest, unknownLabel));
                setDetailItems([]);
            })
            .catch(() => {
                if (!cancelled) {
                    setHighestItems([]);
                    setLowestItems([]);
                    setDetailItems([]);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [accountId, unknownLabel, fetchRange.key, isDetailMode]);

    return {
        highestItems,
        lowestItems,
        detailItems,
        isLoading,
        isDetailMode,
        hasData: isDetailMode ? detailItems.length > 0 : highestItems.length > 0 || lowestItems.length > 0
    };
};
